import { useState, useCallback, useEffect } from 'react';
import { GraduationCap, Save, Trash2, X } from 'lucide-react';
import { trpc } from '@org/clients/school';
import { toast } from 'sonner';
import { cn } from '../../../../utils';
import { Button } from '../../../core/button';
import { TextArea } from '../../../core/textarea';

interface TeacherComment {
  comment: string;
  updatedAt: string;
  teacherId: string;
}

interface TeacherCommentEditorProps {
  submissionId: string;
  existingComment?: TeacherComment | null;
  onCommentSaved?: (comment: TeacherComment | null) => void;
}

export function TeacherCommentEditor({
  submissionId,
  existingComment,
  onCommentSaved,
}: TeacherCommentEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [comment, setComment] = useState(existingComment?.comment ?? '');
  const [isDirty, setIsDirty] = useState(false);

  // Sync with external updates
  useEffect(() => {
    if (!isEditing) {
      setComment(existingComment?.comment ?? '');
    }
  }, [existingComment?.comment, isEditing]);

  const utils = trpc.useUtils();
  const saveMutation = trpc.exercises.saveTeacherComment.useMutation({
    onSuccess: (data) => {
      setIsEditing(false);
      setIsDirty(false);
      onCommentSaved?.(data.teacherComment as TeacherComment | null);
      utils.exercises.getLatestSubmissions.invalidate();
      utils.exercises.getSubmissionHistory.invalidate();
      toast.success(data.teacherComment ? 'Comment saved' : 'Comment removed');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save comment');
    },
  });

  const handleSave = useCallback(() => {
    saveMutation.mutate({
      submissionId,
      comment: comment.trim() || undefined,
    });
  }, [saveMutation, submissionId, comment]);

  const handleDelete = useCallback(() => {
    saveMutation.mutate({
      submissionId,
      comment: undefined,
    });
  }, [saveMutation, submissionId]);

  const handleCancel = useCallback(() => {
    setComment(existingComment?.comment ?? '');
    setIsEditing(false);
    setIsDirty(false);
  }, [existingComment?.comment]);

  const handleChange = useCallback((value: string) => {
    setComment(value);
    setIsDirty(true);
  }, []);

  const hasExistingComment = Boolean(existingComment?.comment);
  const isLoading = saveMutation.isPending;

  // Not editing and no comment - show "Add comment" button
  if (!isEditing && !hasExistingComment) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setIsEditing(true)}
        className="h-auto gap-2 rounded-lg border-dashed border-violet-200 bg-violet-50/50 px-3 py-2 font-normal text-violet-700 hover:border-violet-300 hover:bg-violet-100/50 hover:text-violet-700"
      >
        <GraduationCap className="h-4 w-4" />
        <span>Add teacher comment</span>
      </Button>
    );
  }

  // Not editing but has comment - show read-only view with edit button
  if (!isEditing && hasExistingComment) {
    return (
      <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <GraduationCap className="mt-0.5 h-4 w-4 text-violet-600" />
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wide text-violet-600">
                Teacher Comment
              </p>
              <p className="whitespace-pre-wrap text-sm text-violet-900">
                {existingComment?.comment}
              </p>
              {existingComment?.updatedAt && (
                <p className="text-xs text-violet-500">
                  Updated {new Date(existingComment.updatedAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="text-violet-600 hover:bg-violet-100 hover:text-violet-700"
          >
            Edit
          </Button>
        </div>
      </div>
    );
  }

  // Editing mode
  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/70 p-3">
      <div className="flex items-center gap-2 mb-2">
        <GraduationCap className="h-4 w-4 text-violet-600" />
        <p className="text-xs font-medium uppercase tracking-wide text-violet-600">
          Teacher Comment
        </p>
      </div>
      <TextArea
        value={comment}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Add feedback for this student..."
        rows={3}
        maxLength={5000}
        disabled={isLoading}
        className={cn(
          'min-h-0 resize-none border-violet-200 bg-white text-gray-900 placeholder:text-gray-400',
          'focus-visible:border-violet-400 focus-visible:ring-1 focus-visible:ring-violet-400 focus-visible:ring-offset-0'
        )}
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-violet-500">
          {comment.length} / 5000 characters
        </span>
        <div className="flex items-center gap-2">
          {hasExistingComment && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={isLoading}
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Delete
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <X className="mr-1 h-3 w-3" />
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={isLoading || (!isDirty && hasExistingComment)}
            className="bg-violet-600 hover:bg-violet-700"
          >
            <Save className="mr-1 h-3 w-3" />
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
